import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Edit, Plus, Trash2, UserPlus, LogOut } from 'lucide-react';
import { teamService, LeaveTeamRequest } from '@/services/teamService';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { Team, TeamMemberRole } from '@/types/team';
import { useApiError } from '@/hooks/useApiError';
import { useTranslation } from 'react-i18next';
import CaptainReassignmentModal from './CaptainReassignmentModal';
import { trackFeatureUsage } from '@/analytics/AnalyticsTracker';

const TEAM_MEMBER_ROLES: TeamMemberRole[] = [
  TeamMemberRole.PLAYERS,
  TeamMemberRole.SUBSTITUTES,
  TeamMemberRole.COACH,
  TeamMemberRole.MANAGER,
];

interface TeamActionsProps {
  children?: ReactNode;
  onInvitePlayer?: () => void;
  onDeleteTeam: () => void;
  onEditTeam?: () => void;
  onViewTeamProfile?: () => void;
  onParticipateClick?: () => void;
  isOwner: boolean;
  isAdmin: boolean;
  teamId: string;
  team: Team;
  onTeamUpdate?: () => void;
  isJoining?: boolean;
}

const TeamActions = ({
  children,
  onInvitePlayer,
  onDeleteTeam,
  onEditTeam,
  onViewTeamProfile,
  onParticipateClick,
  isOwner,
  isAdmin,
  teamId,
  team,
  onTeamUpdate,
  isJoining = false,
}: TeamActionsProps) => {
  const { user } = useAuth();
  const { handleApiError } = useApiError();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showCaptainReassignment, setShowCaptainReassignment] = useState(false);
  const { t } = useTranslation('teamaction');
  const { t: tTeam } = useTranslation('team');
  const { t: tTeamSection } = useTranslation('teamsection');

  const isTeamMember = () => {
    if (!user) return false;
    const allMembers = TEAM_MEMBER_ROLES.flatMap(
      role => team.members[role] || [],
    );
    return allMembers.some(member => member.id === user.id);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    trackFeatureUsage('team', 'delete_submitted', { team_id: teamId });
    try {
      await teamService.deleteTeam(teamId);
      trackFeatureUsage('team', 'deleted', {
        team_id: teamId,
        clan_id: team.clanId,
      });
      toast.success(t('modal.deleteSuccess'));
      onDeleteTeam();
    } catch (error) {
      trackFeatureUsage('team', 'delete_failed', {
        team_id: teamId,
        error: error instanceof Error ? error.message : String(error),
      });
      handleApiError(error, {
        defaultMessage: t('toast.deleteError'),
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleLeaveTeam = async () => {
    if (!user) return;

    const isCurrentUserCaptain = team.members.captain?.id === user.id;
    if (isCurrentUserCaptain) {
      const eligiblePlayers = (team.members.players || []).filter(
        player => player.id !== user.id,
      );
      if (eligiblePlayers.length === 0) {
        // No other players exist to assign as captain.
        // Allow the captain to leave.
        await executeLeaveTeam();
        return;
      }
      setShowCaptainReassignment(true);
      return;
    }

    await executeLeaveTeam();
  };

  const executeLeaveTeam = async (newCaptainId?: string) => {
    if (!user) return;
    setIsLeaving(true);
    toast.loading(t('toast.leaving'));
    try {
      const allTeamMembers = TEAM_MEMBER_ROLES.flatMap(
        role => team.members[role] || [],
      );
      const currentTeamMember = allTeamMembers.find(
        member => member.id === user.id,
      );
      if (!currentTeamMember) {
        toast.dismiss();
        toast.error(t('toast.membershipError'));
        setIsLeaving(false);
        return;
      }

      const payload: LeaveTeamRequest = {
        teamId: team._id,
        userId: currentTeamMember._id,
        newCaptainId,
      };
      await teamService.leaveTeam(payload);
      trackFeatureUsage('team', 'left', {
        team_id: team._id,
        clan_id: team.clanId,
        reassigned_captain: !!newCaptainId,
      });
      await onTeamUpdate?.();
      toast.dismiss();
      toast.success(t('toast.leaveSuccess'));
    } catch (error) {
      trackFeatureUsage('team', 'leave_failed', {
        team_id: team._id,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.dismiss();
      handleApiError(error, {
        defaultMessage: t('toast.leaveError'),
      });
    } finally {
      setIsLeaving(false);
    }
  };

  const canParticipate = (isOwner || isAdmin) && !isTeamMember();
  const canLeave = (isOwner || isAdmin) && isTeamMember();

  return (
    <>
      <div className='flex w-full flex-wrap items-center justify-center gap-4 mb-8'>
        {onInvitePlayer && (
          <Button
            onClick={onInvitePlayer}
            className='flex items-center bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90'
          >
            <Plus className='w-4 h-4 me-2' />
            {t('modal.invitePlayer')}
          </Button>
        )}

        {canParticipate && (
          <Button
            onClick={onParticipateClick}
            className='flex items-center bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90'
            disabled={isJoining}
          >
            <UserPlus className='w-4 h-4 me-2' />
            {isJoining ? t('modal.joining') : t('modal.participate')}
          </Button>
        )}

        {canLeave && (
          <Button
            variant='outline'
            onClick={handleLeaveTeam}
            disabled={isLeaving}
            className='flex items-center text-orange-400 border-orange-400/40 hover:bg-orange-400/10'
          >
            <LogOut
              className='w-4 h-4 me-2'
              style={{ transform: 'scaleX(-1)' }}
            />
            {isLeaving ? t('modal.leaving') : t('modal.leave')}
          </Button>
        )}

        {(isOwner || isAdmin) && (
          <Button
            variant='outline'
            onClick={() => setShowDeleteDialog(true)}
            className='flex items-center text-red-400 border-red-400/20 hover:bg-red-400/10'
            disabled={isDeleting}
          >
            <Trash2 className='w-4 h-4 me-2' />
            {isDeleting ? t('modal.deleting') : t('modal.delete')}
          </Button>
        )}

        {onViewTeamProfile && (
          <Button
            type='button'
            variant='outline'
            onClick={onViewTeamProfile}
            className='flex items-center text-purple-300 border-purple-400/20 hover:bg-purple-400/10'
          >
            {tTeam('banner.teamProfile')}
          </Button>
        )}

        {(isOwner || isAdmin) && onEditTeam && (
          <Button
            type='button'
            variant='outline'
            onClick={onEditTeam}
            className='flex items-center text-cyan-300 border-cyan-400/20 hover:bg-cyan-400/10'
          >
            <Edit className='w-4 h-4 me-2' />
            {tTeam('banner.editTeam')}
          </Button>
        )}

        {children}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className='w-[calc(100vw-2rem)] sm:w-full bg-gray-900 border-red-500/20 text-white max-w-md'>
          <AlertDialogHeader>
            <AlertDialogTitle className='text-red-400'>
              {t('modal.deleteTeam')}
            </AlertDialogTitle>
            <AlertDialogDescription className='text-gray-300'>
              {t('modal.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className='bg-gray-700 text-white border-gray-600 hover:bg-gray-600'>
              {t('modal.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className='bg-red-600 hover:bg-red-700 text-white'
              disabled={isDeleting}
            >
              {isDeleting ? t('modal.deleting') : t('modal.confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CaptainReassignmentModal
        isOpen={showCaptainReassignment}
        onClose={() => setShowCaptainReassignment(false)}
        players={(team.members.players || []).filter(
          player => player.id !== user?.id,
        )}
        onConfirm={async newCaptainId => {
          await executeLeaveTeam(newCaptainId);
          setShowCaptainReassignment(false);
        }}
        title={tTeamSection('captainReassignment.title')}
        description={tTeamSection('captainReassignment.description')}
        isSubmitting={isLeaving}
      />
    </>
  );
};

export default TeamActions;
